import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtDate, fmtDateTime, fmtEuro } from "@/lib/format";
import { Field } from "@/components/Common";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { OWNERSHIP, V_STATUS, COST_KINDS, MAINT_KINDS } from "@/components/fleet/fleetConst";

const Section = ({ title, children }) => (
  <div className="mt-6"><div className="micro-label mb-2 border-b pb-1">{title}</div>{children}</div>
);

export const VehicleSheet = ({ vehicleId, onClose, refreshKey }) => {
  const { nameOf } = useData();
  const [v, setV] = useState(null);
  useEffect(() => {
    if (vehicleId) api.get(`/fleet/vehicles/${vehicleId}`).then((r) => setV(r.data)).catch(() => {});
    else setV(null);
  }, [vehicleId, refreshKey]);
  const name = (x) => (x?.team_id ? nameOf("teams", x.team_id) : x?.employee_id ? nameOf("employees", x.employee_id) : x?.assignee_name || "—");
  return (
    <Sheet open={!!vehicleId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="vehicle-sheet">
        {v && <>
          <SheetHeader><SheetTitle className="font-display text-2xl"><span className="font-mono">{v.plate}</span> · {v.model}</SheetTitle></SheetHeader>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <Field label="Società" value={nameOf("companies", v.company_id, "legal_name")} />
            <Field label="Stato" value={V_STATUS[v.status]} />
            <Field label="Tipologia" value={`${OWNERSHIP[v.ownership]}${v.lessor ? ` · ${v.lessor}` : ""}`} />
            <Field label="Fine contratto" value={v.contract_due && fmtDate(v.contract_due)} mono />
            <Field label="Assegnatario" value={name(v)} />
            <Field label="Sede" value={v.branch_id && nameOf("branches", v.branch_id)} />
            <Field label="Assicurazione" value={v.insurance_due && `${fmtDate(v.insurance_due)} ${v.insurer}`} mono />
            <Field label="Revisione" value={v.revision_due && fmtDate(v.revision_due)} mono />
            <Field label="Bollo" value={v.tax_due && fmtDate(v.tax_due)} mono />
            <Field label="Km attuali" value={(v.current_km || 0).toLocaleString("it-IT")} mono />
          </div>
          {v.notes && <p className="mt-4 border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-sm">{v.notes}</p>}
          <Section title="Manutenzioni">
            {v.maintenance.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna</p> : v.maintenance.map((m) => (
              <div key={m.id} className="flex justify-between border-b py-1.5 text-sm"><span>{MAINT_KINDS[m.kind]} {m.description}</span>
                <span className="font-mono text-xs">{m.status === "DONE" ? `eseguita ${fmtDate(m.done.done_date)}` : `entro ${m.due_date ? fmtDate(m.due_date) : ""} ${m.due_km ? `${m.due_km} km` : ""}`}</span></div>
            ))}
          </Section>
          <Section title={`Costi · totale ${fmtEuro(v.costs.reduce((s, c) => s + c.amount, 0))}`}>
            {v.costs.length === 0 ? <p className="text-sm text-muted-foreground">Nessuno</p> : v.costs.slice(0, 30).map((c) => (
              <div key={c.id} className="flex justify-between border-b py-1.5 text-sm"><span>{fmtDate(c.date)} · {COST_KINDS[c.kind]} {c.description}</span><span className="font-mono">{fmtEuro(c.amount)}</span></div>
            ))}
          </Section>
          <Section title="Storico assegnazioni">
            {v.assignments.length === 0 ? <p className="text-sm text-muted-foreground">Nessun cambio registrato</p> : v.assignments.map((a) => (
              <div key={a.id} className="border-b py-1.5 text-sm">{fmtDateTime(a.at)} · {name(a.from)} → <strong>{name(a.to)}</strong> <span className="text-xs text-muted-foreground">({a.by})</span></div>
            ))}
          </Section>
        </>}
      </SheetContent>
    </Sheet>
  );
};
